import React, { useState, useEffect } from 'react';
import { X, Plus, Settings, Lock, Globe, Trash2, Edit3, Filter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SavedFilter, FilterDifference } from '../hooks/useSavedFilters';
import { useAuth } from '../contexts/AuthContext';

interface SaveFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentFilters: { [key: string]: any };
  onSaveFilter: (name: string, isPublic: boolean) => Promise<void>;
  onUpdateFilter: (filterId: string, name: string, isPublic: boolean) => Promise<void>;
  onDeleteFilter: (filterId: string) => Promise<void>;
  onLoadFilter: (filters: { [key: string]: any }, filterName?: string) => void;
  savedFilters: SavedFilter[];
  matchingFilter?: SavedFilter | null;
  filterDifference?: FilterDifference | null;
  hasActiveFilters: boolean;
  isApplyingFilter?: boolean;
}

const SaveFilterModal: React.FC<SaveFilterModalProps> = ({
  isOpen,
  onClose,
  currentFilters,
  onSaveFilter,
  onUpdateFilter,
  onDeleteFilter,
  onLoadFilter,
  savedFilters,
  matchingFilter,
  filterDifference,
  hasActiveFilters,
  isApplyingFilter = false
}) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  
  const [filterName, setFilterName] = useState('');
  const [, setFilterDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [editingFilter, setEditingFilter] = useState<SavedFilter | null>(null);

  // Resetear estado cuando se abre/cierra el modal
  useEffect(() => {
    if (isOpen) {
      setFilterName('');
      setFilterDescription('');
      setIsPublic(true);
      setError(null);
      setShowSaveForm(false);
      setEditingFilter(null);
    }
  }, [isOpen]);

  // Debug: Log saved filters when they change
  useEffect(() => {
    // console.log('SaveFilterModal - savedFilters:', savedFilters);
    // console.log('SaveFilterModal - savedFilters.length:', savedFilters.length);
  }, [savedFilters]);

  // Manejar guardado de filtro
  const handleSave = async () => {
    // console.log('SaveFilterModal - handleSave called', {
    //   filterName,
    //   isPublic,
    //   currentFilters
    // });
    
    if (!filterName.trim()) {
      setError('El nombre del filtro es requerido');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editingFilter) {
        await onUpdateFilter(editingFilter.id!, filterName.trim(), isPublic);
      } else {
        // console.log('SaveFilterModal - calling onSaveFilter');
        await onSaveFilter(filterName.trim(), isPublic);
      }
      
      // console.log('SaveFilterModal - save successful');
      setShowSaveForm(false);
      setEditingFilter(null);
      // No cerrar el modal inmediatamente para dar tiempo a Firebase
    } catch (err) {
      // console.error('SaveFilterModal - save error:', err);
      setError(err instanceof Error ? err.message : 'Error al guardar el filtro');
    } finally {
      setLoading(false);
    }
  };

  // Manejar eliminación de filtro
  const handleDelete = async (filterId: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este filtro?')) {
      return;
    }

    setLoading(true);
    try {
      await onDeleteFilter(filterId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar el filtro');
    } finally {
      setLoading(false);
    }
  };

  // Editar filtro existente
  const handleEdit = (filter: SavedFilter) => {
    setEditingFilter(filter);
    setFilterName(filter.name);
    setFilterDescription(filter.description || '');
    setIsPublic(filter.isPublic);
    setShowSaveForm(true);
  };

  // Cargar filtro
  const handleLoadFilter = (filter: SavedFilter) => {
    onLoadFilter(filter.filters, filter.name);
    onClose();
  };

  // Actualizar filtro existente con cambios actuales
  const handleUpdateWithCurrentFilters = async () => {
    if (!matchingFilter) return;

    setLoading(true);
    try {
      await onUpdateFilter(matchingFilter.id!, matchingFilter.name, matchingFilter.isPublic);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al actualizar el filtro');
    } finally {
      setLoading(false);
    }
  };

  // Renderizar diferencias de filtros
  const renderFilterDifference = () => {
    if (!filterDifference) return null;

    const { changed, added, removed } = filterDifference;
    const hasChanges = Object.keys(changed).length > 0 || Object.keys(added).length > 0 || Object.keys(removed).length > 0;

    if (!hasChanges) return null;

    return (
      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h4 className="text-sm font-medium text-yellow-800 mb-2">Cambios detectados:</h4>
        
        {Object.keys(changed).length > 0 && (
          <div className="mb-2">
            <p className="text-xs font-medium text-yellow-700">Modificados:</p>
            <ul className="text-xs text-yellow-600 ml-2">
              {Object.keys(changed).map(key => (
                <li key={key}>• {key}</li>
              ))}
            </ul>
          </div>
        )}
        
        {Object.keys(added).length > 0 && (
          <div className="mb-2">
            <p className="text-xs font-medium text-green-700">Agregados:</p>
            <ul className="text-xs text-green-600 ml-2">
              {Object.keys(added).map(key => (
                <li key={key}>• {key}</li>
              ))}
            </ul>
          </div>
        )}
        
        {Object.keys(removed).length > 0 && (
          <div>
            <p className="text-xs font-medium text-red-700">Eliminados:</p>
            <ul className="text-xs text-red-600 ml-2">
              {Object.keys(removed).map(key => (
                <li key={key}>• {key}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('filters.saved_filters.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Mostrar mensaje de carga cuando se están aplicando filtros */}
          {isApplyingFilter && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
                <span className="text-sm text-blue-700">{t('filters.saved_filters.applying')}</span>
              </div>
            </div>
          )}
          
          {/* Filtro coincidente o cambios detectados */}
          {matchingFilter && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-blue-900">
                    Filtro guardado: "{matchingFilter.name}"
                  </h3>
                  <p className="text-xs text-blue-700 mt-1">
                    Los filtros actuales coinciden exactamente con este filtro guardado
                  </p>
                </div>
                <button
                  onClick={() => handleEdit(matchingFilter)}
                  className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                  disabled={matchingFilter.createdBy !== currentUser?.uid}
                >
                  <Edit3 className="h-3 w-3" />
                  <span>Editar</span>
                </button>
              </div>
            </div>
          )}

          {filterDifference && matchingFilter && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-yellow-900">
                    Filtro modificado: "{matchingFilter.name}"
                  </h3>
                  <p className="text-xs text-yellow-700 mt-1">
                    Has modificado un filtro guardado. ¿Qué deseas hacer?
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleUpdateWithCurrentFilters}
                    className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    disabled={loading || matchingFilter.createdBy !== currentUser?.uid}
                  >
                    <Plus className="h-3 w-3" />
                    <span>{t('filters.saved_filters.update')}</span>
                  </button>
                  <button
                    onClick={() => setShowSaveForm(true)}
                    className="flex items-center space-x-1 px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200"
                    disabled={loading}
                  >
                    <Plus className="h-3 w-3" />
                    <span>{t('filters.saved_filters.save_as_new')}</span>
                  </button>
                </div>
              </div>
              {renderFilterDifference()}
            </div>
          )}

          {/* Botón para guardar nuevo filtro */}
          {hasActiveFilters && !showSaveForm && !matchingFilter && (
            <div className="mb-6">
              <button
                onClick={() => setShowSaveForm(true)}
                className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50"
                disabled={loading}
              >
                <Plus className="h-4 w-4" />
                <span>{t('filters.saved_filters.save_current')}</span>
              </button>
            </div>
          )}

          {/* Formulario para guardar */}
          {showSaveForm && (
            <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingFilter ? t('filters.saved_filters.edit') : t('filters.saved_filters.save_current')}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('filters.saved_filters.name_required')} *
                  </label>
                  <input
                    type="text"
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                    placeholder={t('filters.saved_filters.name_placeholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={isPublic}
                      onChange={() => setIsPublic(true)}
                      className="mr-2"
                    />
                    <Globe className="h-4 w-4 mr-1 text-green-600" />
                    <span className="text-sm">{t('filters.saved_filters.public')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={!isPublic}
                      onChange={() => setIsPublic(false)}
                      className="mr-2"
                    />
                    <Lock className="h-4 w-4 mr-1 text-gray-600" />
                    <span className="text-sm">{t('filters.saved_filters.private')}</span>
                  </label>
                </div>

                {error && (
                  <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                    {error}
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    onClick={handleSave}
                    disabled={loading || !filterName.trim()}
                    className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50"
                  >
                    <Plus className="h-4 w-4" />
                    <span>{loading ? t('filters.saved_filters.saving') : (editingFilter ? t('filters.saved_filters.update') : t('filters.saved_filters.save'))}</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowSaveForm(false);
                      setEditingFilter(null);
                    }}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {t('filters.saved_filters.cancel')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Lista de filtros guardados */}
          <div>
            {savedFilters.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Settings className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>{t('filters.saved_filters.no_saved_filters')}</p>
                <p className="text-sm">{t('filters.saved_filters.no_saved_filters_help')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {savedFilters.map((filter) => (
                  <div
                    key={filter.id}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    {/* Header con título, icono y botones */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium text-gray-900">{filter.name}</h4>
                        {filter.isPublic ? (
                          <div title="Filtro público">
                            <Globe className="h-4 w-4 text-green-600" />
                          </div>
                        ) : (
                          <div title="Filtro privado">
                            <Lock className="h-4 w-4 text-gray-600" />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleLoadFilter(filter)}
                          disabled={isApplyingFilter}
                          className={`px-2 py-1 text-xs rounded ${
                            isApplyingFilter 
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                          title={isApplyingFilter ? t('filters.saved_filters.loading') : t('filters.saved_filters.load')}
                        >
                          <Filter className="h-3 w-3" />
                        </button>
                        {filter.createdBy === currentUser?.uid && (
                          <>
                            <button
                              onClick={() => handleEdit(filter)}
                              className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                              title={t('filters.saved_filters.edit')}
                            >
                              <Edit3 className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => handleDelete(filter.id!)}
                              className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                              disabled={loading}
                              title={t('filters.saved_filters.delete')}
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Información del creador y fecha de creación */}
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-3 tabular-nums">
                      <span>
                        {t('filters.saved_filters.created_by')}{' '}
                        <strong>
                          {filter.createdByName}
                          {filter.createdByLastname ? ` ${filter.createdByLastname}` : ''}
                        </strong>
                        {filter.createdByEmail ? <span className="text-gray-400"> ({filter.createdByEmail})</span> : null}
                        {filter.createdAt ? (
                          <span className="text-gray-400">
                            {' · '}
                            {filter.createdAt.toDate().toLocaleString('es-AR', {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit', hour12: false,
                            })}
                          </span>
                        ) : null}
                      </span>
                    </div>
                    
                    {/* Pills de filtros ocupando todo el ancho */}
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(filter.filters).map(([key, value]) => {
                        // Formatear el valor para mostrar
                        let displayValue = value;
                        if (Array.isArray(value)) {
                          displayValue = value.join(', ');
                        } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                          // Handle objects by converting to string representation
                          displayValue = Object.entries(value).map(([k, v]) => `${k}: ${v}`).join(', ');
                        } else if (typeof value === 'string' && value.includes('|')) {
                          displayValue = value.split('|').join(', ');
                        }
                        
                        // Skip search_operators as it's handled with global_search
                        if (key === 'search_operators') return null;
                        
                        // Formatear la clave para que sea más legible usando traducciones
                        let displayKey = key;
                        if (key === 'global_search') displayKey = t('filters.search_placeholder') || 'Búsqueda';
                        else if (key === 'sitio') displayKey = t('columns.sitio') || 'Sitio';
                        else if (key === 'marca') displayKey = t('columns.marca') || 'Marca';
                        else if (key === 'categoria') displayKey = t('columns.categoria') || 'Categoría';
                        else if (key === 'price_from') displayKey = t('filters.price_from') || 'Precio desde';
                        else if (key === 'price_to') displayKey = t('filters.price_to') || 'Precio hasta';
                        else if (key === 'max_page') displayKey = t('filters.max_page_label') || 'Página máx';
                        else if (key === 'max_order') displayKey = t('filters.max_order_label') || 'Orden máx';
                        else if (key === 'descuento_filter') displayKey = t('filters.discount_filter') || 'Descuentos';
                        
                        // Formatear valores especiales
                        if (key === 'descuento_filter' && displayValue === 'with_discount') {
                          displayValue = t('filters.with_discount') || 'Con descuento';
                        } else if (key === 'descuento_filter' && displayValue === 'without_discount') {
                          displayValue = t('filters.without_discount') || 'Sin descuento';
                        }
                        
                        // Handle global_search with operators
                        if (key === 'global_search' && filter.filters.search_operators) {
                          const operators = filter.filters.search_operators as { [key: string]: string };
                          const searchTerms = displayValue.split(' ').filter((term: string) => term.length > 0);
                          const formattedTerms = searchTerms.map((term: string) => {
                            const operator = operators[term];
                            if (operator && operator !== 'OR') {
                              return `${term} (${operator})`;
                            }
                            return term;
                          }).join(', ');
                          displayValue = formattedTerms;
                        }
                        
                        return (
                            <span
                              key={key}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700"
                              title={`${displayKey}: ${displayValue}`}
                            >
                              <span className="font-semibold">{displayKey}:</span>
                              <span className="ml-1">{displayValue}</span>
                            </span>
                          );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaveFilterModal; 